import { type ReactiveController, type ReactiveControllerHost, type TemplateResult, html } from "lit";

import type { Ref } from "lit/directives/ref.js";

import type { DropPosition } from "./dnd-container-controller";

/**
 * Controller for managing drop indicator visual feedback
 * Works with DnDContainerController to show drop zones
 */
export class DnDDropIndicatorController implements ReactiveController {
  private containerElement: HTMLElement | null = null;

  constructor(
    private host: ReactiveControllerHost,
    private containerRef: Ref<HTMLElement>,
  ) {
    host.addController(this);
  }

  hostUpdated() {
    // Update container element reference if it changed
    const element = this.containerRef.value || null;
    if (this.containerElement !== element) {
      this.containerElement = element;
    }
  }

  /**
   * Render drop indicator based on current drop position and valid positions
   */
  renderDropIndicator(
    dropPosition: DropPosition | null,
    validPositions: ("top" | "right" | "bottom" | "left")[],
    isDragging: boolean,
  ): TemplateResult {
    if (!isDragging || !dropPosition || !this.containerElement || validPositions.length === 0) {
      return html``;
    }

    // Calculate orientation from valid positions
    const orientation = this.getDropIndicatorOrientation(validPositions);

    // Calculate position relative to the target element
    const targetRect = dropPosition.targetElement.getBoundingClientRect();
    const containerRect = this.containerElement.getBoundingClientRect();

    let left = 0;
    let top = 0;
    let width = "2px";
    let height = "2px";

    if (orientation === "horizontal") {
      // Top/bottom drops: horizontal indicator
      left = targetRect.left - containerRect.left;
      width = `${targetRect.width}px`;

      top =
        dropPosition.side === "top"
          ? targetRect.top - containerRect.top - 1
          : targetRect.bottom - containerRect.top + 1;
    } else {
      // Left/right drops: vertical indicator
      top = targetRect.top - containerRect.top;
      height = `${targetRect.height}px`;
      left =
        dropPosition.side === "left"
          ? Math.max(2, targetRect.left - containerRect.left - 2)
          : targetRect.right - containerRect.left + 1;
    }

    return html`
      <div
        class="drop-indicator ${orientation} active"
        style="
          position: absolute;
          left: ${left}px;
          top: ${top}px;
          width: ${width};
          height: ${height};
          background-color: var(--primary-color, #007bff);
          border-radius: 1px;
          z-index: 1000;
          pointer-events: none;
        "
      ></div>
    `;
  }

  /**
   * Calculate drop indicator orientation from valid drop positions
   */
  private getDropIndicatorOrientation(
    validPositions: ("top" | "right" | "bottom" | "left")[],
  ): "horizontal" | "vertical" {
    // If valid positions include top/bottom → horizontal indicators
    if (validPositions.includes("top") || validPositions.includes("bottom")) {
      return "horizontal";
    }
    // If valid positions include left/right → vertical indicators
    if (validPositions.includes("left") || validPositions.includes("right")) {
      return "vertical";
    }
    // Default fallback
    return "horizontal";
  }
}
